/**
 * Canteen Service - API client for the canteen menu, prepaid wallet,
 * spending controls, and the order lifecycle (place -> accept -> preparing
 * -> ready -> completed, with cancel/reject + refund).
 */
import api from '../utils/api';

const canteenService = {
  // ---- Menu (admin + shared catalog) ----

  listItems: async () => {
    try {
      return await api.get('/api/canteen-wallet/items');
    } catch (error) {
      console.error('Error fetching canteen items:', error);
      throw error;
    }
  },

  createItem: async (data) => {
    try {
      return await api.post('/api/canteen-wallet/items', data);
    } catch (error) {
      console.error('Error creating canteen item:', error);
      throw error;
    }
  },

  deleteItem: async (itemId) => {
    try {
      return await api.delete(`/api/canteen-wallet/items/${itemId}`);
    } catch (error) {
      console.error('Error deleting canteen item:', error);
      throw error;
    }
  },

  getVendors: async () => {
    try {
      return await api.get('/api/canteen-wallet/vendors');
    } catch (error) {
      console.error('Error fetching canteen vendors:', error);
      throw error;
    }
  },

  // ---- Student wallet ----

  getStudentSummary: async () => {
    try {
      return await api.get('/api/canteen-wallet/student/summary');
    } catch (error) {
      console.error('Error fetching student wallet summary:', error);
      throw error;
    }
  },

  getStudentHistory: async (limit) => {
    try {
      return await api.get('/api/canteen-wallet/student/history', { params: { limit } });
    } catch (error) {
      console.error('Error fetching student wallet history:', error);
      throw error;
    }
  },

  // ---- Parent wallet ----

  getParentSummary: async (studentId) => {
    try {
      return await api.get('/api/canteen-wallet/parent/summary', { params: { student_id: studentId } });
    } catch (error) {
      console.error('Error fetching parent wallet summary:', error);
      throw error;
    }
  },

  getParentHistory: async (studentId, limit) => {
    try {
      return await api.get('/api/canteen-wallet/parent/history', { params: { student_id: studentId, limit } });
    } catch (error) {
      console.error('Error fetching parent wallet history:', error);
      throw error;
    }
  },

  topUpWallet: async ({ studentId, amount }) => {
    try {
      return await api.post('/api/canteen-wallet/parent/topup', { student_id: studentId, amount });
    } catch (error) {
      console.error('Error initiating wallet top-up:', error);
      throw error;
    }
  },

  // ---- Spending controls (parent) ----

  getWalletControls: async (studentId) => {
    try {
      return await api.get('/api/canteen-wallet/parent/controls', { params: { student_id: studentId } });
    } catch (error) {
      console.error('Error fetching spending controls:', error);
      throw error;
    }
  },

  updateWalletControls: async (studentId, updates) => {
    try {
      return await api.put('/api/canteen-wallet/parent/controls', updates, { params: { student_id: studentId } });
    } catch (error) {
      console.error('Error updating spending controls:', error);
      throw error;
    }
  },

  // ---- Counter staff: scan-to-identify ----
  // Reads a student's existing ID-card QR (routers/id_cards.py's card_number)
  // to identify who to charge, instead of the student being logged in
  // themselves — see KioskLoginPage.js/BarcodeScanner.js for the same
  // scan-first pattern used elsewhere.

  scanCard: async (cardNumber) => {
    try {
      return await api.get(`/api/canteen-wallet/scan/${encodeURIComponent(cardNumber)}`);
    } catch (error) {
      console.error('Error scanning student card:', error);
      throw error;
    }
  },

  // ---- Orders ----

  // studentId is set only by counter staff placing an order on a student's
  // behalf after a card scan; a student caller always orders for themselves
  // and this is ignored server-side for them.
  placeOrder: async ({ items, note, studentId }) => {
    try {
      return await api.post('/api/canteen-wallet/orders', { items, note, student_id: studentId });
    } catch (error) {
      console.error('Error placing canteen order:', error);
      throw error;
    }
  },

  listOrders: async ({ studentId, status, limit } = {}) => {
    try {
      return await api.get('/api/canteen-wallet/orders', {
        params: { student_id: studentId, order_status: status, limit },
      });
    } catch (error) {
      console.error('Error fetching canteen orders:', error);
      throw error;
    }
  },

  getOrder: async (orderId) => {
    try {
      return await api.get(`/api/canteen-wallet/orders/${orderId}`);
    } catch (error) {
      console.error('Error fetching order detail:', error);
      throw error;
    }
  },

  cancelOrder: async (orderId) => {
    try {
      return await api.post(`/api/canteen-wallet/orders/${orderId}/cancel`, {});
    } catch (error) {
      console.error('Error cancelling order:', error);
      throw error;
    }
  },

  // ---- Canteen staff / admin ----

  updateOrderStatus: async (orderId, status, note) => {
    try {
      return await api.post(`/api/canteen-wallet/orders/${orderId}/status`, { status, note });
    } catch (error) {
      console.error('Error updating order status:', error);
      throw error;
    }
  },

  verifyPickup: async (orderId, pickupCode) => {
    try {
      return await api.post(`/api/canteen-wallet/orders/${orderId}/verify-pickup`, { pickup_code: pickupCode });
    } catch (error) {
      console.error('Error verifying pickup:', error);
      throw error;
    }
  },
};

export default canteenService;
